import { AQUA_BLUE, IGNIS_RED, LIGHTEST_GRAY } from "@120wateraudit/waterworks";
import React from "react";
import { Dropdown } from "semantic-ui-react";
import styled from "styled-components";
import { useTooltip } from "../Tooltip/Tooltip";
import { CNCSubmission, SubmissionStatus } from "src/types/Submission";
import { getOptionsForCurrentStatus } from "./StatusChangeDropdown";

export const getColorFromCommunicationStatus = (
  status: SubmissionStatus
): string => {
  switch (status) {
    case SubmissionStatus.submitted:
      return "#BFDBFE";
    case SubmissionStatus.approved:
      return "#BBF7D0";
    case SubmissionStatus.rejected:
      return "#FECACA";
    case SubmissionStatus.notSubmitted:
      return "#D3D3D3";
    case SubmissionStatus.inReview:
      return "#FDE68A";
    case SubmissionStatus.voided:
      return "#ffc72c";
    case SubmissionStatus.processingSubmission:
      return AQUA_BLUE;
    case SubmissionStatus.failed:
      return IGNIS_RED;
    default:
      return LIGHTEST_GRAY;
  }
};

interface StatusBadgeProps {
  submissionCommunication: CNCSubmission;
  pwsName: string;
  pwsId: number;
  submissionPeriodId: number;
  openChangeSubmissionCommunicationStatus: any;
  refetch: () => Promise<any>;
}

const SubmissionCommunicationStatusBadgeVersionTwo: React.FC<
  StatusBadgeProps
> = ({
  submissionCommunication,
  pwsName,
  pwsId,
  submissionPeriodId,
  openChangeSubmissionCommunicationStatus,
  refetch,
}) => {
  const status: SubmissionStatus = submissionCommunication
    ? (submissionCommunication.status as SubmissionStatus)
    : ("notSubmitted" as SubmissionStatus);

  const statusOptions = getOptionsForCurrentStatus();

  const { setTooltipContent, setTooltipPosition } = useTooltip();

  const handleChange = (
    _: React.SyntheticEvent<HTMLElement>,
    { value }: any
  ): void => {
    openChangeSubmissionCommunicationStatus(
      value,
      pwsId,
      submissionPeriodId,
      pwsName,
      submissionCommunication?.id,
      refetch
    );
  };

  return (
    <StyledDropdown
      onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition(rect.x + rect.width, rect.y + rect.height - 90);
        setTooltipContent(
          <span>Change the user&apos;s Customer Notice Submission Status</span>
        );
      }}
      onMouseLeave={() => setTooltipContent(null)}
      value={status}
      options={
        submissionCommunication
          ? statusOptions
          : [{ text: "Not Submitted", value: "notSubmitted" }]
      }
      onChange={handleChange}
      selection
      style={dropdownStyle(
        submissionCommunication
          ? SubmissionStatus[
              submissionCommunication.status as keyof typeof SubmissionStatus
            ]
          : SubmissionStatus["notSubmitted" as keyof typeof SubmissionStatus]
      )}
    />
  );
};

const dropdownStyle = (status: SubmissionStatus): Object => ({
  backgroundColor: getColorFromCommunicationStatus(status),
  borderRadius: "5px",
  minWidth: "145px",
  fontWeight: "500",
  padding: "3px",
  minHeight: "unset",
  textAlign: "center",
});

const StyledDropdown = styled(Dropdown)<{ value: string }>`
  min-width: 120px;
  text-align: center;
  background-color: ${({ value }) =>
    getColorFromCommunicationStatus(value as SubmissionStatus)};
  color: white;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  .dropdown.icon {
    top: 2px !important;
  }
  border: none !important;
`;

export default SubmissionCommunicationStatusBadgeVersionTwo;
